import { useEffect, useRef, type ReactNode } from 'react';
import { getGsap, hasFinePointer, prefersReducedMotion } from '@/lib/motion';

// Magnetic — pulls its child toward the pointer while the pointer is near it,
// then springs back when it leaves. Mouse/trackpad only: on a phone it renders
// the child untouched, and reduced-motion visitors never see it move.
//
// Wrap a button or link: <Magnetic><a …>Make a video</a></Magnetic>. The
// wrapper is an inline-block span, so layout and the child's own hit area are
// unchanged; `strength` is how far (as a share of the pointer's offset from
// the centre) the child follows.

export function Magnetic({
  children,
  strength = 0.35,
  radius = 90,
  className = '',
}: {
  children: ReactNode;
  strength?: number;
  /** Extra px around the element where the pull still applies. */
  radius?: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !hasFinePointer() || prefersReducedMotion()) return undefined;
    const { gsap } = getGsap();
    const xTo = gsap.quickTo(el, 'x', { duration: 0.6, ease: 'elastic.out(1, 0.4)' });
    const yTo = gsap.quickTo(el, 'y', { duration: 0.6, ease: 'elastic.out(1, 0.4)' });

    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const near = Math.abs(dx) < r.width / 2 + radius && Math.abs(dy) < r.height / 2 + radius;
      xTo(near ? dx * strength : 0);
      yTo(near ? dy * strength : 0);
    };
    const reset = () => { xTo(0); yTo(0); };
    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerleave', reset);
    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', reset);
      gsap.killTweensOf(el);
    };
  }, [strength, radius]);

  return (
    <span ref={ref} className={`inline-block will-change-transform ${className}`} data-magnetic="">
      {children}
    </span>
  );
}
